import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SearchPage from './pages/SearchPage';
import ProfileViewPage from './pages/ProfileViewPage';
import ChatPage from './pages/ChatPage';
import PlansPage from './pages/PlansPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminKYCPage from './pages/admin/AdminKYCPage';
import AdminModerationPage from './pages/admin/AdminModerationPage';
import AdminRevenuePage from './pages/admin/AdminRevenuePage';
import ProfileEditPage from './pages/ProfileEditPage';
import KYCPage from './pages/KYCPage';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading, needsRegistration } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[#78716C]">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user && !needsRegistration) return <Navigate to="/auth" />;
  if (needsRegistration && !user) return <Navigate to="/register" />;
  return children;
}

function AppRoutes() {
  const { user, needsRegistration } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<><Navbar /><LandingPage /></>} />
      <Route path="/auth" element={
        user ? <Navigate to="/dashboard" /> : <><Navbar /><AuthPage /></>
      } />
      <Route path="/register" element={
        user && !needsRegistration ? <Navigate to="/dashboard" /> :
        <><Navbar /><RegisterPage /></>
      } />
      <Route path="/dashboard" element={<ProtectedRoute><Navbar /><DashboardPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><Navbar /><SearchPage /></ProtectedRoute>} />
      <Route path="/profile/:id" element={<ProtectedRoute><Navbar /><ProfileViewPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Navbar /><ChatPage /></ProtectedRoute>} />
      <Route path="/chat/:chatId" element={<ProtectedRoute><Navbar /><ChatPage /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><Navbar /><ProfileEditPage /></ProtectedRoute>} />
      <Route path="/kyc" element={<ProtectedRoute><Navbar /><KYCPage /></ProtectedRoute>} />
      <Route path="/plans" element={<><Navbar /><PlansPage /></>} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="kyc" element={<AdminKYCPage />} />
        <Route path="moderation" element={<AdminModerationPage />} />
        <Route path="revenue" element={<AdminRevenuePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <div className="min-h-screen bg-[#FFF8F0]">
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
